import { useCallback, useState } from "react";
import { useLang } from "@/lib/lang";
import { createCase } from "@/lib/api";
import { invalidateCases, invalidateDocuments, prependDocument } from "@/lib/cache/invalidate";
import CaseQararkPicker from "@/components/CaseQararkPicker";
import { clearCreateQararkSuggestCaches } from "@/lib/qararkSuggestCache";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import LibraryPickerDialog from "@/components/LibraryPickerDialog";
import UploadDropzone from "@/components/UploadDropzone";
import { toast } from "sonner";

const PRACTICE_AREAS = ["civil", "criminal"];

const DOC_ROLES = ["law", "regulation", "case_detail", "reference"];

const ROLE_KEYS = {
  law: "case_doc_role_law",
  regulation: "case_doc_role_regulation",
  case_detail: "case_doc_role_case_detail",
  reference: "case_doc_role_reference",
};

export default function CaseCreateDialog({
  open,
  onOpenChange,
  documents = [],
  onCreated,
}) {
  const { t } = useLang();
  const [step, setStep] = useState(0);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [practiceArea, setPracticeArea] = useState("civil");
  const [pendingDocs, setPendingDocs] = useState([]);
  const [selectedQararkItems, setSelectedQararkItems] = useState([]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerRole, setPickerRole] = useState("reference");
  const [submitting, setSubmitting] = useState(false);

  const reset = () => {
    clearCreateQararkSuggestCaches();
    setStep(0);
    setTitle("");
    setDescription("");
    setPracticeArea("civil");
    setPendingDocs([]);
    setSelectedQararkItems([]);
  };

  const handleClose = (value) => {
    if (!value) reset();
    onOpenChange(value);
  };

  const addLinkedDoc = (docId, role) => {
    setPendingDocs((prev) => {
      const filtered = prev.filter((d) => d.document_id !== docId);
      return [...filtered, { document_id: docId, role, sort_order: filtered.length }];
    });
  };

  const handleUpload = async (meta, role) => {
    addLinkedDoc(meta.id, role);
    prependDocument(undefined, meta);
  };

  const handleQararkSelection = useCallback((items) => {
    setSelectedQararkItems(items);
  }, []);

  const handleSubmit = async () => {
    if (!title.trim()) {
      toast.error(t("case_title"));
      return;
    }
    setSubmitting(true);
    try {
      const caseData = await createCase({
        title: title.trim(),
        description: description.trim(),
        practice_area: practiceArea,
        documents: pendingDocs,
        qarark_items: selectedQararkItems,
      });
      await invalidateCases();
      if (selectedQararkItems.length) await invalidateDocuments();
      onCreated?.(caseData);
      reset();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent className="rounded-none max-w-lg grid-border p-0 gap-0">
          <DialogHeader className="grid-border-b px-6 py-4">
            <DialogTitle className="font-serif-display text-xl">{t("case_create")}</DialogTitle>
            <p className="text-xs text-slate-500 mt-1">
              {step === 0 ? t("case_create_step_info") : t("case_create_step_docs")}
            </p>
          </DialogHeader>

          <div className="px-6 py-5 space-y-4 max-h-[70vh] overflow-y-auto">
            {step === 0 && (
              <>
                <div>
                  <label className="eyebrow block mb-2">{t("case_title")}</label>
                  <input
                    data-testid="case-title-input"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    className="w-full grid-border px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-slate-900"
                  />
                </div>
                <div>
                  <label className="eyebrow block mb-2">{t("case_practice_area")}</label>
                  <select
                    data-testid="case-practice-select"
                    value={practiceArea}
                    onChange={(e) => setPracticeArea(e.target.value)}
                    className="w-full grid-border px-3 py-2 text-sm bg-white outline-none"
                  >
                    {PRACTICE_AREAS.map((key) => (
                      <option key={key} value={key}>
                        {t(`practice_${key}`)}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="eyebrow block mb-2">{t("case_description")}</label>
                  <Textarea
                    data-testid="case-description-input"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    rows={4}
                    className="rounded-none grid-border text-sm resize-none"
                  />
                </div>
              </>
            )}

            {step === 1 && (
              <div className="space-y-4">
                <CaseQararkPicker
                  caseMeta={{
                    title,
                    description,
                    practice_area: practiceArea,
                  }}
                  active={step === 1}
                  onSelectionChange={handleQararkSelection}
                />
                {DOC_ROLES.map((role) => (
                  <div key={role} className="grid-border p-4">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-sm font-medium text-slate-900">
                        {t(ROLE_KEYS[role])}
                      </span>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="rounded-none h-7 text-[10px]"
                        onClick={() => {
                          setPickerRole(role);
                          setPickerOpen(true);
                        }}
                      >
                        {t("case_link_library")}
                      </Button>
                    </div>
                    <UploadDropzone
                      compact
                      onUploaded={(meta) => handleUpload(meta, role)}
                    />
                    <div className="mt-2 space-y-1">
                      {pendingDocs
                        .filter((d) => d.role === role)
                        .map((d) => {
                          const doc = documents.find((x) => x.id === d.document_id);
                          return (
                            <div key={d.document_id} className="text-xs text-slate-600 truncate">
                              {doc?.filename || d.document_id}
                            </div>
                          );
                        })}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="grid-border-t px-6 py-4 flex justify-between">
            {step > 0 ? (
              <Button
                variant="outline"
                className="rounded-none"
                onClick={() => setStep(0)}
              >
                {t("case_prev")}
              </Button>
            ) : (
              <div />
            )}
            {step === 0 ? (
              <Button
                className="rounded-none bg-slate-900 text-white"
                onClick={() => setStep(1)}
                disabled={!title.trim()}
              >
                {t("case_next")}
              </Button>
            ) : (
              <Button
                data-testid="case-create-submit"
                className="rounded-none bg-slate-900 text-white"
                onClick={handleSubmit}
                disabled={submitting}
              >
                {t("case_create_submit")}
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <LibraryPickerDialog
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        documents={documents}
        excludeIds={pendingDocs.map((d) => d.document_id)}
        onSelect={(docId) => {
          addLinkedDoc(docId, pickerRole);
          setPickerOpen(false);
        }}
      />
    </>
  );
}
